import { BackButton } from "@/components/shared/BackButton";
import { StyledActionButton } from "@/components/shared/StyledActionButton";
import { formatDateToMonthDay } from "@/utils/date";
import {
  Box,
  Center,
  Divider,
  Group,
  List,
  Stack,
  Text,
  Timeline,
  Title,
  useMantineTheme,
} from "@mantine/core";
import {
  IconAirBalloon,
  IconCircleCheck,
  IconDice5,
  IconFriends,
  IconGlassGin,
  IconGlobe,
  IconLock,
  IconToolsKitchen2,
  IconToolsKitchen3,
  IconUser,
  IconYoga,
} from "@tabler/icons-react";
import { useMemo } from "react";

interface Props {
  selectedDate: Date;
  onNextButtonClick: () => void;
  onBackButtonClick: () => void;
}

// TODO: replace these hard-coded sample pools with real group data once available.
const PEOPLE_HINT_POOL = [
  "두바이를 10번 이상 다녀왔어요!",
  "137kg에서 60kg 감량했습니다 ㅎㅎ",
  "혼자 800km 걷고 왔어요!",
  "세계 90개국 이상 여행했어요 🌍",
  "가나에서 비누 팔았던 사람이에요!",
  "인도에서 버스킹하다 현지인 돌잔치에서 노래 불렀어요 ㅋㅋ",
  "프랑스 외인부대 지원 합격했어요",
  "자전거로 전국 일주 했어요!",
  "5개월간 9,000km 자전거 여행 다녀왔어요!",
  "시베리아 횡단열차로만 27일 여행했어요",
  "퇴사 후 5대륙 10개국 여행했어요!",
  "캄보디아에서 9년 살다 왔어요",
  "작곡한 곡이 캐나다 필름 페스티벌에 노미네이션됐어요!",
  "치과위생사였는데 지금은 성교육 강사에요 ㅎㅎ",
  "방금 유치원 퇴사했어요!",
  "전직 카레이서에요, 우승도 했어요!",
  "이직을 7번 했어요..",
  "생각보다 떡을 잘 만들어요!",
  "아이들 13년 가르치다 그만두고 동화책 그리고 있어요",
  "제 작품이 OTT 플랫폼에 올라가 있어요 🤫",
  "즉흥으로 세계여행 한 파워 J예요!",
  "올해 안 해본 도전들을 하나씩 해보는 중이에요",
  "전생체험 해봤어요!",
  "폴댄스가 취미인데 클럽은 한 번도 안 가봤어요 ㅋㅋ",
  "혼자 국토대장정 했어요!",
  "피부에 글씨를 쓸 수 있어요",
  "날다람쥐 키워요!",
  "에버랜드에서 일하다 간호사가 됐어요!",
  "중국어만 할 줄 아는데 혼자 아이슬란드 다녀왔어요",
  "오늘 막 전역했어요!",
  "퇴사하고 새로운 거 시도하는 중이에요!",
  "맛집 저장 리스트 1,000개 넘어요!",
  "매일 아침 5시 반에 일어나요",
  "4년째 매일 다이어리 쓰는 중이에요!",
  "올해만 제주도 6번 다녀왔어요",
  "첫 회사 8년째 다니고 있어요!",
  "요즘 텃밭 가꾸는 재미에 살아요",
  "하루에 빵 한 개는 꼭 먹어야 해요!",
  "혼자 해외여행 즐기는 여행 초보예요!",
  "친해지면 말이 엄청 많아져요 😆",
  "카페 투어가 요즘 제일 행복해요!",
  "직장인 밴드에서 베이스 치고 있어요!",
  "최근에 러닝에 완전 빠졌어요!",
  "서울 상경한 지 얼마 안 됐어요!",
  "취미가 10개 넘어요, 취미 부자예요 ㅎㅎ",
  "점심 먹으면서 저녁 메뉴 고민해요 🤤",
  "N잡으로 4가지 일 하고 있어요!",
  "요가로 다리 찢기 성공했어요!",
  "생일마다 컬러 증명사진 찍어요!",
  "주 5일 검도 수련해요 (귀살대 아님)",
  "밥은 걸러도 디저트는 꼭 챙겨요!",
  "좋아하는 영화 40~50번 봤어요",
  "2025년에 영화 130편 봤어요!",
  "매년 노래를 4만 분씩 들어요!",
  "10달 동안 떠서 스웨터 완성했어요!",
  "1년에 음악 페스티벌 7개 다녀요!",
  "오늘 이 모임 신청이 인생 최대의 용기였어요",
  "평소엔 관찰자 모드인데 오늘만큼은 참가자로 나왔어요!",
  "집에 가고 싶다는 말 달고 사는데 정작 집엔 잘 안 가요 ㅋㅋ",
  "심각한 집순이인데 오늘 큰 용기 냈어요!",
];

// Sourced from the "profession" question options (excluding the "기타" catch-all).
const FIELD_POOL = ["IT", "예술", "의료", "정치/공공", "요식업", "사업", "금융"];

const pickRandom = <T,>(items: readonly T[], count: number): T[] => {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, count);
};

const hintCardStyle = {
  backgroundColor: "rgba(255, 255, 255, 0.04)",
  border: "1px solid rgba(255, 255, 255, 0.06)",
  borderRadius: 20,
};

const quotePillStyle = {
  backgroundColor: "rgba(214, 190, 112, 0.08)",
  border: "1px solid rgba(214, 190, 112, 0.35)",
  borderRadius: 14,
  padding: "12px 16px",
};

const tagPillStyle = {
  backgroundColor: "rgba(255, 255, 255, 0.03)",
  border: "1px solid rgba(255, 255, 255, 0.18)",
  borderRadius: 999,
  padding: "6px 18px",
};

const lockedPillStyle = {
  backgroundColor: "rgba(255, 255, 255, 0.04)",
  border: "1px solid rgba(255, 255, 255, 0.12)",
  borderRadius: 10,
  padding: "6px 16px",
};

export const HowItWorksBeta = ({ selectedDate, onNextButtonClick, onBackButtonClick }: Props) => {
  const theme = useMantineTheme();

  const peopleHints = useMemo(() => pickRandom(PEOPLE_HINT_POOL, 2), []);
  const fieldHints = useMemo(() => pickRandom(FIELD_POOL, 3), []);

  const groupHintDate = new Date(selectedDate);
  groupHintDate.setDate(groupHintDate.getDate() - 2);
  const formattedGroupHintDate = formatDateToMonthDay(groupHintDate.toISOString());

  const curationDate = new Date(selectedDate);
  curationDate.setDate(curationDate.getDate() - 1);
  const formattedCurationDate = formatDateToMonthDay(curationDate.toISOString());

  const dinnerDate = new Date(selectedDate);
  const formattedDinnerDate = formatDateToMonthDay(dinnerDate.toISOString());

  return (
    <Stack pb="xl" pt="lg">
      <Center pos="relative" mih={50}>
        <BackButton onClick={onBackButtonClick} />
      </Center>
      <Stack>
        <Title order={1} c="white" mb="xl" ta="center" size="2rem">
          어떻게 진행되나요?
        </Title>
        <Text ta="center">이번 저녁 식사, 누구와 어디서 함께 하게 될까요?</Text>
        <Center mt="xl">
          <Timeline
            active={1}
            bulletSize={30}
            lineWidth={2}
            color={theme.colors.yellow[0]}
            style={{ maxWidth: "75%" }}
          >
            <Timeline.Item
              bullet={<IconCircleCheck size={20} color="black" />}
              title={
                <Text c="white" fw={700} size="lg">
                  예약 확정
                </Text>
              }
            >
              <Text c="dimmed" size="sm">
                예약 요청 후 함께할 그룹이 만들어지면 저녁 식사 초대 확정을 문자로 알려드려요.
              </Text>
            </Timeline.Item>
            <Timeline.Item
              bullet={<IconFriends size={20} color="black" />}
              title={
                <Text c="white" size="lg">
                  <b>저녁 식사 하루 전</b>
                </Text>
              }
            >
              <Text c="dimmed" size="sm" pb="md">
                이번 오프닝 테이블에서 함께할 그룹의 힌트가 공개돼요.
              </Text>
              <Box p="lg" style={hintCardStyle}>
                <Stack gap="lg">
                  <Stack gap="sm">
                    <Group gap={8} wrap="nowrap">
                      <IconUser size={18} color={theme.colors.gray[5]} />
                      <Text c="dimmed" size="sm">
                        우리 테이블에는 어떤 사람이 있을까?
                      </Text>
                    </Group>
                    <Stack gap={8}>
                      {peopleHints.map((hint) => (
                        <Box key={hint} style={quotePillStyle}>
                          <Text size="sm" fw={500} style={{ color: "rgba(245, 236, 210, 0.95)" }}>
                            {`"${hint}"`}
                          </Text>
                        </Box>
                      ))}
                    </Stack>
                    <Group gap={6} wrap="nowrap">
                      <IconLock size={14} color={theme.colors.gray[6]} />
                      <Text c="dimmed" size="xs">
                        +2명 D-1 공개
                      </Text>
                    </Group>
                  </Stack>

                  <Divider color="rgba(255, 255, 255, 0.08)" />

                  <Stack gap="sm">
                    <Group gap={8} wrap="nowrap">
                      <IconToolsKitchen2 size={18} color={theme.colors.gray[5]} />
                      <Text c="dimmed" size="sm">
                        우리 테이블은 어떤 분야가 있을까?
                      </Text>
                    </Group>
                    <Group gap="xs">
                      {fieldHints.map((field) => (
                        <Box key={field} style={tagPillStyle}>
                          <Text c="gray.2" size="sm" fw={500}>
                            {field}
                          </Text>
                        </Box>
                      ))}
                      <Group gap={6} wrap="nowrap" ml={4}>
                        <IconLock size={14} color={theme.colors.gray[6]} />
                        <Text c="dimmed" size="xs">
                          +1명 D-1 공개
                        </Text>
                      </Group>
                    </Group>
                  </Stack>

                  <Divider color="rgba(255, 255, 255, 0.08)" />

                  <Stack gap="sm">
                    <Group gap={8} wrap="nowrap">
                      <IconGlobe size={18} color={theme.colors.gray[5]} />
                      <Text c="dimmed" size="sm">
                        우리 테이블에는 어떤 국적이 있을까?
                      </Text>
                    </Group>
                    <Group gap={8} wrap="nowrap">
                      <IconLock size={14} color={theme.colors.gray[6]} />
                      <Box style={lockedPillStyle}>
                        <Text c="dimmed" size="xs">
                          D-1 공개
                        </Text>
                      </Box>
                    </Group>
                  </Stack>
                </Stack>
              </Box>
            </Timeline.Item>
            <Timeline.Item
              bullet={<IconAirBalloon size={20} color="black" />}
              title={
                <Text c="white" size="lg">
                  <b>저녁 식사 당일</b>
                </Text>
              }
            >
              <Text c="dimmed" size="sm" pb="md">
                이번 주 큐레이터가 준비한 정보를 공개해요.
              </Text>
              <List c="white">
                <List.Item c="dimmed" icon={<IconToolsKitchen3 size={20} />}>
                  이번주 큐레이팅 된 레스토랑 장소는?
                </List.Item>
                <List.Item c="dimmed" icon={<IconYoga size={20} />}>
                  이번주 큐레이터는?
                </List.Item>
                <List.Item c="dimmed" icon={<IconDice5 size={20} />}>
                  이번주 큐레이팅 된 게임은?
                </List.Item>
                <List.Item c="dimmed" icon={<IconGlassGin size={20} />}>
                  이번주 큐레이팅 된 에프터 장소는?
                </List.Item>
              </List>
            </Timeline.Item>
            <Timeline.Item
              bullet={<IconToolsKitchen2 size={20} />}
              title={
                <Text c="white" size="lg">
                  <b>7시 저녁 식사</b>
                </Text>
              }
            >
              <Text c="dimmed" size="sm">
                예약된 장소에서 즐거운 저녁 식사를 즐겨보세요!
              </Text>
            </Timeline.Item>
          </Timeline>
        </Center>
      </Stack>

      <Center style={{ marginTop: "auto" }}>
        <StyledActionButton mt="xs" size="lg" onClick={onNextButtonClick}>
          <Text>다음</Text>
        </StyledActionButton>
      </Center>
    </Stack>
  );
};
